import { TokenUsageWithBreakdown } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { FormattedTokenUsageDataPoint, TokenUsageStats } from '../types';
import { convertBigIntStringToNumber } from './tokenDataTransformers';

/**
 * Calculates token usage statistics from raw API data
 *
 * Computes total, input, output, maximum, and average tokens across all data points.
 * Uses the raw API data to accurately track input vs output tokens separately.
 *
 * @param usageData Raw token usage data from the API
 * @param formattedUsageData Array of formatted data points (for max/avg per period)
 * @param groups Array of group names present in the data
 * @returns Object containing calculated statistics
 */
export function calculateTokenUsageStats(
  usageData: TokenUsageWithBreakdown[] | undefined,
  formattedUsageData: FormattedTokenUsageDataPoint[] | undefined,
  groups: string[]
): TokenUsageStats {
  if (!usageData || usageData.length === 0 || !formattedUsageData || formattedUsageData.length === 0) {
    return {
      totalTokens: 0,
      inputTokens: 0,
      outputTokens: 0,
      maxTokens: 0,
      averageTokens: 0,
      uniqueCount: groups.length,
    };
  }

  // Calculate totals from raw API data for accurate input/output breakdown
  let totalInputTokens = 0;
  let totalOutputTokens = 0;

  usageData.forEach((datum) => {
    const usageBreakdown = datum.usageBreakdown || [];
    usageBreakdown.forEach((breakdown) => {
      totalInputTokens += convertBigIntStringToNumber(breakdown.usage?.inputTokens);
      totalOutputTokens += convertBigIntStringToNumber(breakdown.usage?.outputTokens);
    });
  });

  const totalTokens = totalInputTokens + totalOutputTokens;

  // Calculate max tokens per period from formatted data
  const maxTokens = formattedUsageData.reduce((max, dataPoint) => {
    const periodTotal = groups.reduce((periodSum, group) => {
      const value = dataPoint[group];
      const numericValue = typeof value === 'number' ? value : 0;
      return periodSum + numericValue;
    }, 0);
    return Math.max(max, periodTotal);
  }, 0);

  // Calculate average tokens per period
  const averageTokens = formattedUsageData.length > 0 ? totalTokens / formattedUsageData.length : 0;

  return {
    totalTokens,
    inputTokens: totalInputTokens,
    outputTokens: totalOutputTokens,
    maxTokens,
    averageTokens,
    uniqueCount: groups.length,
  };
}
